import type { MaterialDefinition, ResourceAmount } from "@/lib/crafting/types";
import { SOCKET_LABELS } from "@/lib/crafting/types";
import type { Planner } from "@/hooks/usePlanner";
import { ResourceLine } from "./ResourceLine";
import { MATERIAL_DISPLAY_ORDER } from "@/lib/crafting/provisional";

export interface PlanSummaryPanelProps {
  planner: Planner;
  materials: Record<string, MaterialDefinition>;
  onReview: () => void;
}

/** Stable resource ordering: known currencies first, then everything else. */
export function sortResources(list: readonly ResourceAmount[]): ResourceAmount[] {
  return [...list].sort((a, b) => {
    const ia = MATERIAL_DISPLAY_ORDER.indexOf(a.itemHash);
    const ib = MATERIAL_DISPLAY_ORDER.indexOf(b.itemHash);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
}

/**
 * The live planning summary. Sits alongside the crafting interface rather than
 * replacing it — the weapon stays the focus.
 *
 * Calculation updates are announced politely so a screen-reader user hears the
 * new required level after changing a perk, without every keystroke firing.
 */
export function PlanSummaryPanel({ planner, materials, onReview }: PlanSummaryPanelProps) {
  const { derived, state } = planner;
  const { progression, cost, blame } = derived;
  const shaping = state.mode === "new-shape";

  return (
    <section
      aria-label="Plan summary"
      className="corner-brackets border border-line-dim bg-black/45 p-5 backdrop-blur-sm"
    >
      <h2 className="type-tracked mb-4 text-[0.65rem] text-ink-2">Plan Summary</h2>

      <div aria-live="polite" aria-atomic="true" className="space-y-3">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-ink-2">Required weapon level</span>
          <span className="text-2xl leading-none font-semibold tabular-nums">
            {progression.requiredWeaponLevel}
          </span>
        </div>

        <div className="flex items-baseline justify-between gap-4 text-sm">
          <span className="text-ink-2">Remaining progress</span>
          <span className="tabular-nums">
            {progression.remainingLevels === 0
              ? "None"
              : `${progression.remainingLevels.toFixed(2)} levels`}
          </span>
        </div>

        <p
          className={`text-sm ${progression.alreadyAvailable ? "text-ok" : "text-level"}`}
        >
          {progression.alreadyAvailable
            ? "✓ Every selected component is unlocked at your current level."
            : `Locked until level ${progression.requiredWeaponLevel}${
                blame ? ` — ${blame.component.name} (${SOCKET_LABELS[blame.key]})` : ""
              }.`}
        </p>
      </div>

      {!progression.alreadyAvailable && progression.estimatedKills !== undefined && (
        <div className="mt-4 border-t border-line-dim pt-3">
          <h3 className="label-caps mb-2 text-xs text-ink-3">
            Estimated grind
            <span className="ml-2 normal-case tracking-normal text-ink-3">(rough estimates)</span>
          </h3>
          <ul className="space-y-1 text-sm text-ink-2">
            <li className="flex justify-between gap-4">
              <span>Enemy defeats</span>
              <span className="tabular-nums">
                ~{progression.estimatedKills.toLocaleString()}
              </span>
            </li>
            {progression.estimatedActivities.slice(0, 3).map((a) => (
              <li key={a.label} className="flex justify-between gap-4">
                <span>{a.label}</span>
                <span className="tabular-nums">~{a.completions}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 space-y-4 border-t border-line-dim pt-3">
        {shaping && cost.shapeNow.length > 0 && (
          <CostGroup title="Shape now" resources={cost.shapeNow} materials={materials} />
        )}
        {cost.reshapeLater.length > 0 && (
          <CostGroup
            title={shaping ? "Reshape later" : "Reshape cost"}
            resources={cost.reshapeLater}
            materials={materials}
          />
        )}
        {shaping && cost.shapeNow.length > 0 && cost.reshapeLater.length > 0 && (
          <CostGroup title="Total investment" resources={cost.total} materials={materials} emphasis />
        )}
        {cost.total.length === 0 && (
          <p className="text-sm text-ink-3">
            No resource cost — the plan matches the current configuration.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onReview}
        className="type-button mt-5 w-full border border-line bg-white/10 px-4 py-2.5 text-xs transition-colors hover:bg-white/20"
      >
        Review Plan
      </button>
    </section>
  );
}

function CostGroup({
  title,
  resources,
  materials,
  emphasis,
}: {
  title: string;
  resources: ResourceAmount[];
  materials: Record<string, MaterialDefinition>;
  emphasis?: boolean;
}) {
  return (
    <div>
      <h3
        className={`label-caps mb-2 text-xs ${emphasis ? "text-ink" : "text-ink-3"}`}
      >
        {title}
      </h3>
      <ul className="space-y-1">
        {sortResources(resources).map((r) => (
          <li key={r.itemHash}>
            <ResourceLine amount={r} materials={materials} />
          </li>
        ))}
      </ul>
    </div>
  );
}
